/**
 * Web DataSource: the core port implemented with TanStack query-core observers over the persisted cache.
 * Screens never touch TanStack; view models consume these observables.
 */
import type { Channel, CreateAgentRequest, CreateChannelRequest, EventEnvelope, InviteGuestRequest, InviteGuestResponse, RegisterDeviceRequest } from "@bundle/contracts";
import {
  errorKind,
  errorMessage,
  Messages,
  Outbox,
  Workspace,
  type ApiClient,
  type CreateAgentResult,
  type DataSource,
  type EnvelopeResult,
  type LivePagedQuery,
  type LiveQuery,
  type MessagePage,
  type Observable,
  type PagedState,
  type PendingEntry,
  type QueryState,
  type SendInput,
  type WorkspaceCache,
} from "@bundle/core";
import {
  InfiniteQueryObserver,
  MutationObserver,
  QueryObserver,
  type InfiniteQueryObserverResult,
  type QueryClient,
  type QueryObserverResult,
} from "@tanstack/query-core";
import { closeChannelNotifications } from "../push.ts";
import { getOutbox, keys, updateOutbox, updatePages, updateThreads, updateWorkspace, type MessagesData } from "./keys.ts";
import { applyDatabaseEvent, dbKeys, enqueue, fetchDatabase, pendingObservable, resolveId } from "./databases.ts";
import { applyPageEvent, fetchPage, fetchTree, mutatePage, pageKeys, seedTree, updateTree } from "./pages.ts";
import { mutateChannel, reapplyAfterEvent, withPendingChannels } from "./channels.ts";
import type { CreateSidebarFolderRequest, SidebarMove, UpdateChannelRequest, UpdateMembershipRequest, UpdateSidebarFolderRequest } from "@bundle/contracts";
import { Channels, Moderation } from "@bundle/core";
import { Docs, type DocsCache } from "@bundle/core";
import type { CreatePageRequest, CreatePageResponse, GetPageResponse, UpdatePageRequest } from "@bundle/contracts";
import type { DatabaseCache, NewRowInput, NewViewInput, RowPatch } from "@bundle/core";
import type { CellWrite, ReorderRowsResponse, SortRowsRequest, UpdateDatabaseRequest, UpdateViewRequest } from "@bundle/contracts";
import { applySentMessage, SEND_SCOPE, type DeleteVars, type EditVars, readScope, type PinVars, type ReactVars, type ReadVars, type SendVars, type UnreadVars } from "./mutations.ts";
import type { FileInfoResponse } from "@bundle/contracts";
import type { PickedFile, UploadEntry, UploadHandle, UploadTarget } from "@bundle/core";
import { SendGate } from "../files/sendGate.ts";
import type { UploadManager } from "../files/uploads.ts";

const PAGE_SIZE = 50;
const NO_UPLOADS: UploadEntry[] = [];

function toState<T>(r: QueryObserverResult<T> | InfiniteQueryObserverResult<unknown>, data: T | undefined): QueryState<T> {
  const s: QueryState<T> = {
    data,
    status: r.status,
    fetching: r.isFetching,
    paused: r.fetchStatus === "paused",
    updatedAt: r.dataUpdatedAt,
  };
  if (r.error !== null) {
    s.error = errorMessage(r.error);
    s.errorKind = errorKind(r.error);
  }
  return s;
}

/** Wraps an observer as a LiveQuery with a stable snapshot per observer result. */
function live<R extends { status: string }, S>(observer: { subscribe: (l: (r: R) => void) => () => void; getCurrentResult: () => R; refetch: () => Promise<unknown>; destroy: () => void }, map: (r: R) => S) {
  let lastResult: R | undefined;
  let lastState: S | undefined;
  const snapshot = (): S => {
    const r = observer.getCurrentResult();
    if (r !== lastResult || lastState === undefined) {
      lastResult = r;
      lastState = map(r);
    }
    return lastState;
  };
  return {
    // TanStack notifies synchronously inside its own dispatch; a throwing listener would silently stall the
    // fetch, so errors are reported instead of propagated.
    subscribe: (listener: () => void) =>
      observer.subscribe(() => {
        try {
          listener();
        } catch (e) {
          console.error("[data] listener failed", e);
        }
      }),
    getSnapshot: snapshot,
    refresh: async () => {
      await observer.refetch();
    },
    dispose: () => observer.destroy(),
  };
}

export class QueryDataSource implements DataSource {
  private readonly client: QueryClient;
  private readonly api: ApiClient;
  private readonly meId: () => string;
  private readonly outboxes = new Map<string, Observable<PendingEntry[]>>();
  /** Files (platform/files): the upload queue and the outbox gate for messages with attachments. */
  private uploadQueue: UploadManager | undefined;
  private gate: SendGate | undefined;

  constructor(client: QueryClient, api: ApiClient, meId: () => string) {
    this.client = client;
    this.api = api;
    this.meId = meId;
  }

  /** Turns on uploads (the container passes the shared queue). */
  enableFiles(uploads: UploadManager): void {
    this.uploadQueue = uploads;
    this.gate = new SendGate(this.client, uploads, (ws, input, fileIds) => this.mutate<SendVars>("send", { ws, meId: this.meId(), input, fileIds }, SEND_SCOPE));
  }

  workspace(ws: string): LiveQuery<WorkspaceCache> {
    const observer = new QueryObserver<WorkspaceCache>(this.client, {
      queryKey: keys.workspace(ws),
      queryFn: async () => {
        const b = await this.api.bootstrap(ws);
        seedTree(this.client, ws, b);
        return withPendingChannels(this.client, ws, Workspace.fromBootstrap(b));
      },
      // Show the cache instantly, refresh in the background on every start.
      staleTime: 0,
      refetchOnMount: "always",
    });
    return live(observer, (r) => toState(r, r.data));
  }

  messages(ws: string, channelId: string): LivePagedQuery<MessagePage> {
    // A bigger next page while jumping back to an old message (fetchOlder(limit)); refetches use the normal size.
    let nextLimit: number | undefined;
    const observer = new InfiniteQueryObserver<MessagePage, Error, MessagesData, readonly unknown[], number | null>(this.client, {
      queryKey: keys.messages(ws, channelId),
      queryFn: async ({ pageParam }) => {
        const q: { limit: number; before?: number } = { limit: pageParam !== null && nextLimit !== undefined ? nextLimit : PAGE_SIZE };
        if (pageParam !== null) q.before = pageParam;
        const res = await this.api.listMessages(ws, channelId, q);
        return { messages: res.messages.toSorted((a, b) => a.seq - b.seq), hasMore: res.hasMore };
      },
      initialPageParam: null,
      getNextPageParam: (last) => (last.hasMore && last.messages[0] !== undefined ? last.messages[0].seq : undefined),
    });
    const base = live(observer, (r): PagedState<MessagePage> => ({
      ...toState(r, r.data?.pages),
      hasOlder: r.hasNextPage,
      fetchingOlder: r.isFetchingNextPage,
    }));
    return {
      ...base,
      fetchOlder: async (limit?: number) => {
        nextLimit = limit;
        try {
          await observer.fetchNextPage();
        } finally {
          nextLimit = undefined;
        }
      },
    };
  }

  thread(ws: string, channelId: string, rootId: string): LiveQuery<MessagePage> {
    const observer = new QueryObserver<MessagePage>(this.client, {
      queryKey: keys.thread(ws, rootId),
      queryFn: async () => {
        const res = await this.api.listMessages(ws, channelId, { threadRootId: rootId, limit: 200 });
        let messages = res.messages.toSorted((a, b) => a.seq - b.seq);
        if (!messages.some((m) => m.id === rootId)) {
          // Older API versions returned replies only: take the root from the channel history cache.
          const pages = this.client.getQueryData<MessagesData>(keys.messages(ws, channelId))?.pages ?? [];
          const root = Messages.findInPages(pages, rootId);
          if (root !== undefined) messages = [root, ...messages];
        }
        return { messages, hasMore: res.hasMore };
      },
    });
    return live(observer, (r) => toState(r, r.data));
  }

  outbox(ws: string): Observable<PendingEntry[]> {
    const existing = this.outboxes.get(ws);
    if (existing !== undefined) return existing;
    const observer = new QueryObserver<PendingEntry[]>(this.client, {
      queryKey: keys.outbox(ws),
      queryFn: () => getOutbox(this.client, ws),
      staleTime: Infinity,
      gcTime: Infinity,
    });
    const EMPTY: PendingEntry[] = [];
    const o = live(observer, (r) => r.data ?? EMPTY);
    this.outboxes.set(ws, o);
    return o;
  }

  cachedSeq(ws: string): number | undefined {
    return this.client.getQueryData<WorkspaceCache>(keys.workspace(ws))?.seq;
  }

  applyEnvelope(ws: string, env: EventEnvelope, prevSeq?: number): EnvelopeResult {
    const cache = this.client.getQueryData<WorkspaceCache>(keys.workspace(ws));
    if (cache === undefined) return "ignored";
    const update = Workspace.applyEnvelope(cache, env, prevSeq);
    if (update.result !== "applied") return update.result;
    this.client.setQueryData(keys.workspace(ws), update.cache);
    const ev = env.event;
    if (ev.type === "message.created" || ev.type === "message.updated") {
      const channelId = ev.message.channelId;
      updatePages(this.client, ws, channelId, (p) => Messages.applyEventToPages(p, ev, channelId));
    } else if (ev.type === "message.deleted" || ev.type === "reaction.added" || ev.type === "reaction.removed") {
      const channelId = ev.channelId;
      updatePages(this.client, ws, channelId, (p) => Messages.applyEventToPages(p, ev, channelId));
    }
    updateThreads(this.client, ws, (page, rootId) => Messages.applyEventToThread(page, ev, rootId));
    if (ev.type === "message.created") updateOutbox(this.client, ws, (o) => Outbox.reconcileSent(o, ev.message));
    applyDatabaseEvent(this.client, ws, ev, this.meId());
    applyPageEvent(this.client, ws, ev);
    reapplyAfterEvent(this.client, ws, ev);
    return "applied";
  }

  async resync(ws: string): Promise<void> {
    await this.client.refetchQueries({ queryKey: keys.workspace(ws), type: "all" }, { throwOnError: true });
    await this.client.invalidateQueries({ queryKey: keys.messagesAll(ws) });
    await this.client.invalidateQueries({ queryKey: keys.threadsAll(ws) });
    await this.client.invalidateQueries({ queryKey: pageKeys.tree(ws) });
    await this.client.invalidateQueries({ queryKey: pageKeys.pageAll(ws) });
  }

  // ---------- Mutations (queued while offline, persisted) ----------

  private mutate<V>(key: string, vars: V, scope?: { id: string }): void {
    const options: { mutationKey: string[]; scope?: { id: string } } = { mutationKey: [key] };
    if (scope !== undefined) options.scope = scope;
    new MutationObserver<unknown, unknown, V>(this.client, options).mutate(vars).catch(() => {
      // Failures are reflected in the cache (outbox "failed", or a refetch for edits).
    });
  }

  send(ws: string, input: SendInput): void {
    // Messages with attachments wait (shown as pending) until their uploads are stored.
    if (input.uploadIds !== undefined && input.uploadIds.length > 0 && this.gate !== undefined) {
      this.gate.hold(ws, input, this.meId());
      return;
    }
    this.mutate<SendVars>("send", { ws, meId: this.meId(), input }, SEND_SCOPE);
  }

  private removeSendMutations(clientId: string): void {
    const cache = this.client.getMutationCache();
    for (const m of cache.findAll({ mutationKey: ["send"] })) {
      const v = m.state.variables as SendVars | undefined;
      if (v !== undefined && v.input.clientId === clientId && m.state.status !== "pending") cache.remove(m);
    }
  }

  /** Clientids with a live send mutation (pending or paused), e.g. restored from disk. */
  liveSendClientIds(): string[] {
    return this.client
      .getMutationCache()
      .findAll({ mutationKey: ["send"] })
      .filter((m) => m.state.status === "pending" || m.state.isPaused)
      .map((m) => (m.state.variables as SendVars | undefined)?.input.clientId)
      .filter((id): id is string => id !== undefined);
  }

  /** After restore: outbox entries whose mutation wasn't persisted can't complete → mark failed. */
  failOrphanedSends(ws: string): void {
    // Messages waiting for uploads are only orphans if their uploads are gone: wait until those are restored.
    if (this.uploadQueue !== undefined && !this.uploadQueue.restored) {
      void this.uploadQueue.ready.then(() => this.failOrphanedSends(ws));
      return;
    }
    const gate = this.gate;
    const waiting = gate === undefined ? [] : getOutbox(this.client, ws).filter((e) => gate.isWaiting(e)).map((e) => e.message.clientId ?? e.message.id);
    const ids = [...this.liveSendClientIds(), ...waiting];
    updateOutbox(this.client, ws, (o) => Outbox.failOrphans(o, ids));
    gate?.resume(ws);
  }

  retrySend(ws: string, clientId: string): void {
    const entry = getOutbox(this.client, ws).find((e) => e.message.clientId === clientId);
    if (entry === undefined) return;
    this.removeSendMutations(clientId);
    if (entry.uploadIds !== undefined && this.gate !== undefined) {
      this.gate.retry(ws, entry);
      return;
    }
    const m = entry.message;
    const input: SendInput = { clientId, channelId: m.channelId, text: m.text, createdAt: m.createdAt };
    if (m.threadRootId !== undefined) input.threadRootId = m.threadRootId;
    if (m.alsoSentToChannel === true) input.alsoSendToChannel = true;
    if (m.sticker !== undefined) input.sticker = m.sticker;
    if (m.gif !== undefined) input.gif = m.gif;
    this.send(ws, input);
  }

  discardSend(ws: string, clientId: string): void {
    this.removeSendMutations(clientId);
    const entry = getOutbox(this.client, ws).find((e) => e.message.clientId === clientId);
    updateOutbox(this.client, ws, (o) => Outbox.removePending(o, clientId));
    for (const id of entry?.uploadIds ?? []) this.uploadQueue?.remove(id);
  }

  editMessage(ws: string, channelId: string, messageId: string, text: string): void {
    this.mutate<EditVars>("edit", { ws, channelId, messageId, text, at: Date.now() });
  }

  deleteMessage(ws: string, channelId: string, messageId: string): void {
    this.mutate<DeleteVars>("delete", { ws, channelId, messageId });
  }

  setReaction(ws: string, channelId: string, messageId: string, emoji: string, on: boolean): void {
    this.mutate<ReactVars>("react", { ws, channelId, messageId, emoji, on, meId: this.meId() });
  }

  markRead(ws: string, channelId: string, seq: number): void {
    this.mutate<ReadVars>("markRead", { ws, channelId, seq }, readScope(channelId));
    closeChannelNotifications(channelId);
  }

  /** Queued like reactions (works offline; per-message order kept on reconnect); resolves once queued. */
  async setPinned(ws: string, channelId: string, messageId: string, on: boolean): Promise<void> {
    this.mutate<PinVars>("pin", { ws, channelId, messageId, on, meId: this.meId(), at: Date.now() }, { id: `pin:${messageId}` });
  }

  /** Queued like pins (works offline; in order with this conversation's reads); resolves once applied locally. */
  async markUnread(ws: string, channelId: string, seq: number): Promise<void> {
    this.mutate<UnreadVars>("markUnread", { ws, channelId, seq }, readScope(channelId));
  }

  // ---------- Online-only ----------

  async createChannel(ws: string, req: CreateChannelRequest): Promise<Channel> {
    const channel = await this.api.createChannel(ws, req);
    const now = Date.now();
    updateWorkspace(this.client, ws, (c) => {
      const next = { ...c, channels: { ...c.channels, [channel.id]: channel } };
      if (c.memberships[channel.id] !== undefined) return next;
      const ms = { channelId: channel.id, userId: c.meId, lastReadSeq: channel.lastMessageSeq, mentionCount: 0, notify: "default" as const, muted: false, starred: false, joinedAt: now };
      return { ...next, memberships: { ...next.memberships, [channel.id]: ms } };
    });
    return channel;
  }

  // ---------- Channels & my sidebar (platform/data/channels.ts): optimistic, queued offline ----------

  createChannelQueued(ws: string, req: CreateChannelRequest & { id: string }): Promise<Channel> {
    return mutateChannel<Channel>(this.client, { kind: "createChannel", ws, meId: this.meId(), at: Date.now(), req });
  }

  updateChannel(ws: string, channelId: string, req: UpdateChannelRequest): Promise<Channel> {
    return mutateChannel<Channel>(this.client, { kind: "updateChannel", ws, channelId, req });
  }

  async joinChannel(ws: string, channelId: string): Promise<Channel> {
    const channel = await this.api.joinChannel(ws, channelId);
    updateWorkspace(this.client, ws, (c) => Channels.addChannel(c, channel, Date.now()));
    return channel;
  }

  leaveChannel(ws: string, channelId: string): Promise<void> {
    return mutateChannel<void>(this.client, { kind: "leaveChannel", ws, channelId });
  }

  listChannels(ws: string, archived: boolean): Promise<Channel[]> {
    return this.api.listChannels(ws, archived);
  }

  updateMembership(ws: string, channelId: string, req: UpdateMembershipRequest): void {
    mutateChannel(this.client, { kind: "membership", ws, channelId, req }).catch(() => {});
  }

  moveSidebarItems(ws: string, moves: SidebarMove[]): void {
    mutateChannel(this.client, { kind: "moves", ws, moves }).catch(() => {});
  }

  createSidebarFolder(ws: string, req: CreateSidebarFolderRequest & { id: string }): void {
    mutateChannel(this.client, { kind: "createFolder", ws, req }).catch(() => {});
  }

  updateSidebarFolder(ws: string, folderId: string, req: UpdateSidebarFolderRequest): void {
    mutateChannel(this.client, { kind: "updateFolder", ws, folderId, req }).catch(() => {});
  }

  deleteSidebarFolder(ws: string, folderId: string): void {
    mutateChannel(this.client, { kind: "deleteFolder", ws, folderId }).catch(() => {});
  }

  channelMembers(ws: string, channelId: string): Promise<import("@bundle/contracts").ListChannelMembersResponse> {
    return this.api.listChannelMembers(ws, channelId);
  }

  async addChannelMembers(ws: string, channelId: string, userIds: string[]): Promise<Channel> {
    const channel = await this.api.addChannelMembers(ws, channelId, userIds);
    updateWorkspace(this.client, ws, (c) => (c.channels[channel.id] === undefined ? c : { ...c, channels: { ...c.channels, [channel.id]: channel } }));
    return channel;
  }

  async removeChannelMember(ws: string, channelId: string, userId: string): Promise<void> {
    await this.api.removeChannelMember(ws, channelId, userId);
  }

  async setBlocked(ws: string, userId: string, blocked: boolean): Promise<void> {
    const was = Moderation.isBlocked(this.client.getQueryData<WorkspaceCache>(keys.workspace(ws))?.blockedUserIds, userId);
    updateWorkspace(this.client, ws, (c) => Workspace.setBlockedUserIds(c, Moderation.withBlocked(c.blockedUserIds, userId, blocked)));
    try {
      await (blocked ? this.api.blockUser(ws, userId) : this.api.unblockUser(ws, userId));
    } catch (e) {
      // Refused or offline: this person goes back to how they were (the rest of the list is left alone).
      updateWorkspace(this.client, ws, (c) => Workspace.setBlockedUserIds(c, Moderation.withBlocked(c.blockedUserIds, userId, was)));
      throw e;
    }
  }

  async inviteGuest(ws: string, req: InviteGuestRequest): Promise<InviteGuestResponse> {
    const res = await this.api.inviteGuest(ws, req);
    updateWorkspace(this.client, ws, (c) => ({ ...c, members: { ...c.members, [res.member.userId]: res.member } }));
    return res;
  }

  async createAgent(ws: string, req: CreateAgentRequest): Promise<CreateAgentResult> {
    const res = await this.api.createAgent(ws, req);
    updateWorkspace(this.client, ws, (c) => ({ ...c, members: { ...c.members, [res.member.userId]: res.member } }));
    return res;
  }

  // Push devices are person-scoped and online-only (NotificationsViewModel retries on its next sync).
  async registerDevice(req: RegisterDeviceRequest): Promise<void> {
    await this.api.registerDevice(req);
  }

  async unregisterDevice(token: string): Promise<void> {
    await this.api.unregisterDevice(token);
  }

  // ---------- Databases (platform/data/databases.ts) ----------

  database(ws: string, databaseId: string): LiveQuery<DatabaseCache> {
    const observer = new QueryObserver<DatabaseCache>(this.client, {
      queryKey: dbKeys.database(ws, databaseId),
      queryFn: () => fetchDatabase(this.client, this.api, ws, databaseId),
      // Cached schema and rows show at once; refreshed in the background whenever a view opens.
      staleTime: 0,
      refetchOnMount: "always",
    });
    const base = live(observer, (r) => toState(r, r.data));
    // Several view models share a database (a view + the row peek); one created during render subscribes and
    // refetches, which TanStack reports to the others synchronously — notify them after the render instead.
    return { ...base, subscribe: (listener: () => void) => base.subscribe(() => queueMicrotask(listener)) };
  }

  createRow(ws: string, databaseId: string, input: NewRowInput): void {
    enqueue(this.client, { kind: "createRow", ws, databaseId, meId: this.meId(), input });
  }

  updateRow(ws: string, databaseId: string, rowId: string, patch: RowPatch): void {
    enqueue(this.client, { kind: "updateRow", ws, databaseId, rowId, meId: this.meId(), patch });
  }

  deleteRow(ws: string, databaseId: string, rowId: string): void {
    enqueue(this.client, { kind: "deleteRow", ws, databaseId, rowId });
  }

  updateDatabase(ws: string, databaseId: string, req: UpdateDatabaseRequest): void {
    enqueue(this.client, { kind: "updateDatabase", ws, databaseId, req });
  }

  createView(ws: string, databaseId: string, input: NewViewInput): void {
    enqueue(this.client, { kind: "createView", ws, databaseId, input });
  }

  updateView(ws: string, databaseId: string, viewId: string, req: UpdateViewRequest): void {
    enqueue(this.client, { kind: "updateView", ws, databaseId, viewId, req });
  }

  deleteView(ws: string, databaseId: string, viewId: string): void {
    enqueue(this.client, { kind: "deleteView", ws, databaseId, viewId });
  }

  writeCells(ws: string, databaseId: string, writes: CellWrite[]): void {
    enqueue(this.client, { kind: "writeCells", ws, databaseId, meId: this.meId(), clientId: crypto.randomUUID(), writes });
  }

  createSummaryRow(ws: string, databaseId: string, clientId: string, index?: number): void {
    enqueue(this.client, index === undefined ? { kind: "createSummaryRow", ws, databaseId, clientId } : { kind: "createSummaryRow", ws, databaseId, clientId, index });
  }

  moveSummaryRow(ws: string, databaseId: string, summaryRowId: string, index: number): void {
    enqueue(this.client, { kind: "moveSummaryRow", ws, databaseId, summaryRowId, index });
  }

  deleteSummaryRow(ws: string, databaseId: string, summaryRowId: string): void {
    enqueue(this.client, { kind: "deleteSummaryRow", ws, databaseId, summaryRowId });
  }

  async sortRows(ws: string, databaseId: string, req: SortRowsRequest): Promise<ReorderRowsResponse> {
    const res = await this.api.sortRows(ws, databaseId, { ...req, propertyId: resolveId(this.client, ws, req.propertyId) });
    await this.client.invalidateQueries({ queryKey: dbKeys.database(ws, databaseId) });
    return res;
  }

  async reorderRows(ws: string, databaseId: string, order: string[]): Promise<ReorderRowsResponse> {
    const res = await this.api.reorderRows(ws, databaseId, order.map((id) => resolveId(this.client, ws, id)));
    await this.client.invalidateQueries({ queryKey: dbKeys.database(ws, databaseId) });
    return res;
  }

  databasePending(ws: string, databaseId: string): Observable<string[]> {
    return pendingObservable(this.client, ws, databaseId);
  }

  resolveId(ws: string, id: string): string {
    return resolveId(this.client, ws, id);
  }

  // ---------- Pages (tree + metadata; bodies are Loro docs in platform/docs) ----------

  pageTree(ws: string): LiveQuery<DocsCache> {
    const observer = new QueryObserver<DocsCache>(this.client, {
      queryKey: pageKeys.tree(ws),
      queryFn: () => fetchTree(this.client, this.api, ws),
      // Bootstrap seeds and events keep it fresh; refetch in the background now and then.
      staleTime: 60_000,
    });
    return live(observer, (r) => toState(r, r.data));
  }

  page(ws: string, pageId: string): LiveQuery<GetPageResponse> {
    const observer = new QueryObserver<GetPageResponse>(this.client, {
      queryKey: pageKeys.page(ws, pageId),
      queryFn: () => fetchPage(this.client, this.api, ws, pageId),
      staleTime: 0,
      refetchOnMount: "always",
    });
    return live(observer, (r) => toState(r, r.data));
  }

  async createPage(ws: string, req: CreatePageRequest): Promise<CreatePageResponse> {
    const res = await this.api.createPage(ws, req);
    updateTree(this.client, ws, (c) => Docs.upsertPages(c, res.pages));
    return res;
  }

  updatePage(ws: string, pageId: string, req: UpdatePageRequest): void {
    mutatePage(this.client, { kind: "updatePage", ws, pageId, req, meId: this.meId(), at: Date.now() });
  }

  setFavorite(ws: string, pageId: string, on: boolean): void {
    mutatePage(this.client, { kind: "favorite", ws, pageId, on });
  }

  // ---------- Files (platform/files) ----------

  uploadFile(ws: string, file: PickedFile, target: UploadTarget): UploadHandle {
    if (this.uploadQueue === undefined) throw new Error("uploads are not enabled");
    return this.uploadQueue.add(ws, file, target);
  }

  uploads(ws: string): Observable<UploadEntry[]> {
    if (this.uploadQueue === undefined) return { subscribe: () => () => {}, getSnapshot: () => NO_UPLOADS };
    return this.uploadQueue.uploads(ws);
  }

  retryUpload(_ws: string, id: string): void {
    this.uploadQueue?.retry(id);
  }

  removeUpload(_ws: string, id: string): void {
    this.uploadQueue?.remove(id);
  }

  fileInfo(ws: string, fileId: string): LiveQuery<FileInfoResponse> {
    const observer = new QueryObserver<FileInfoResponse>(this.client, {
      queryKey: [...keys.all(ws), "file", fileId],
      queryFn: () => this.api.fileInfo(ws, fileId),
      staleTime: 0,
      refetchOnMount: "always",
    });
    return live(observer, (r) => toState(r, r.data));
  }

  async clear(): Promise<void> {
    this.client.getMutationCache().clear();
    this.client.clear();
    await this.uploadQueue?.clear();
  }
}

export { applySentMessage };
